import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import useItems from '../../hooks/useItems';
import useScrollToTop from '../../hooks/useScrollToTop';
import * as AppPropTypes from '../../lib/PropTypes';

import { useApi } from '../../contexts/ApiContext';
import { useWindowSize } from '../../contexts/SiteContext';
import { useFloatingVideo } from '../../contexts/VideoContext';
import Button from '../buttons/Button';
import PageHeader from '../headers/PageHeader';
import Arrow from '../icons/Arrow';
import Articles from '../lists/Articles';
import Article from '../partials/Article';
import DisableBodyScroll from '../partials/DisableBodyScroll';

import styles from '../../../styles/pages/news.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    pathname: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    pathname: PropTypes.string,
    className: null,
};

const NewsPage = ({ page: newsPage, pathname, className }) => {
    const [, navigate] = useLocation();
    const route = useUrlGenerator();
    const asideRef = useRef(null);

    const { slug = null, url = null, parent = null } = newsPage || {};
    const { url: parentUrl = null, slug: parentSlug = null } = parent || {};

    const [query, setQuery] = useState({ page: 1, count: 10 });

    const api = useApi();
    const getPage = useCallback((p, c, q) => api.pages.getArticles(p, c, q), [api, query]);

    const {
        allItems: items,
        allLoaded,
        loadNextPage,
        loading,
    } = useItems({
        store: 'articles',
        getPage,
        // pages: articles,
        paginated: true,
        query,
        onQueryChange: setQuery,
    });

    const [previousArticle, setPreviousArticle] = useState(null);
    useEffect(() => {
        if (pathname !== parentUrl) {
            setPreviousArticle(slug);
        }
    }, [newsPage, setPreviousArticle]);

    const onNext = useCallback(() => {
        const next =
            items !== null
                ? (items || []).reduce((acc, it, idx) => {
                      const { slug: currentSlug = null } = it || {};
                      if (slug === currentSlug) {
                          if (idx < items.length - 1) {
                              const { slug: nextSlug = null } = items[idx + 1] || {};
                              return nextSlug;
                          }
                      }
                      return acc;
                  }, null)
                : null;
        if (next !== null) {
            navigate(route('page_with_parent', { parent: parentSlug || slug, page: next }));
        }
    }, [navigate, slug, items]);

    const onPrevious = useCallback(() => {
        const previous =
            items !== null
                ? (items || []).reduce((acc, it, idx) => {
                      const { slug: currentSlug = null } = it || {};
                      if (slug === currentSlug) {
                          if (idx > 0) {
                              const { slug: previousSlug = null } = items[idx - 1] || {};
                              return previousSlug;
                          }
                      }
                      return acc;
                  }, null)
                : null;
        if (previous !== null) {
            navigate(route('page_with_parent', { parent: parentSlug || slug, page: previous }));
        }
    }, [navigate, route, slug, parentSlug, items]);

    const { setFloatingVideo } = useFloatingVideo();
    const { width: windowWidth = null, height: windowHeight = null } = useWindowSize();
    const mobile = windowWidth <= 980;

    useEffect(() => {
        if (mobile && pathname === parentUrl && asideRef.current !== null) {
            asideRef.current.focus();
        }
    }, [asideRef.current, mobile, pathname, parentUrl]);

    // Clear the current video
    useEffect(() => {
        setFloatingVideo(null);
    }, [setFloatingVideo]);

    const homeAndMobile = mobile && pathname !== url;
    const newsListOpen = !mobile || homeAndMobile;

    useScrollToTop(newsPage, pathname);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <PageHeader page={newsPage} />
            <DisableBodyScroll toggle={homeAndMobile} toggleMediumScreen={false} />
            <aside
                className={classNames([
                    styles.listContainer,
                    {
                        [styles.opened]: newsListOpen,
                    },
                ])}
                style={{ height: windowHeight }}
                ref={asideRef}
            >
                <Articles
                    className={classNames([
                        styles.list,
                        {
                            [styles.opened]: newsListOpen,
                        },
                    ])}
                    page={newsPage}
                    articles={items}
                    items={items}
                    allLoaded={allLoaded}
                    loadNextPage={loadNextPage}
                    loading={loading}
                    url={null}
                    currentSlug={mobile ? previousArticle : slug}
                    scrollToClick={!mobile}
                />
            </aside>
            <div
                className={classNames([
                    styles.content,
                    {
                        [styles.opened]: !newsListOpen || !mobile,
                    },
                ])}
            >
                <Button href={parentUrl || null} className={styles.back}>
                    <Arrow className={styles.arrow} />
                    <span className={styles.label}>
                        <FormattedMessage
                            defaultMessage="View all news"
                            description="Back button"
                        />
                    </span>
                </Button>
                <Article
                    article={newsPage}
                    className={styles.article}
                    isRounded
                    withControls
                    onNext={onNext}
                    onPrevious={onPrevious}
                />
            </div>
        </div>
    );
};

NewsPage.propTypes = propTypes;
NewsPage.defaultProps = defaultProps;

export default NewsPage;
